import React, { useState,useRef } from 'react';
import { ArrowLeft, Container, Server, Cloud, ChevronDown, ChevronUp, ClipboardList } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';

const ECSPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { ecsData, region, accountName, duration } = location.state || {};
  
  // State for expanded tables and pagination
  const [clusterRowsToShow, setClusterRowsToShow] = useState(10);
  const [serviceRowsToShow, setServiceRowsToShow] = useState(10);
  const [taskRowsToShow, setTaskRowsToShow] = useState(10);
 
  const LongTextCell = ({ text, maxLength = 30 }) => {
       const [showTooltip, setShowTooltip] = useState(false);
       const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
       const tooltipRef = useRef(null);
       const cellRef = useRef(null);
       
       if (!text || text.length <= maxLength) {
         return <span>{text}</span>;
       }
       
       const handleMouseEnter = (e) => {
         setShowTooltip(true);
         setTimeout(() => {
           if (cellRef.current && tooltipRef.current) {
             const cellRect = cellRef.current.getBoundingClientRect();
             const tooltipRect = tooltipRef.current.getBoundingClientRect();
             const viewportHeight = window.innerHeight;
             const spaceBelow = viewportHeight - cellRect.bottom;
             const tooltipHeight = tooltipRect.height;
             
             if (spaceBelow < tooltipHeight) {
               setTooltipPosition({
                 top: -tooltipHeight - 5,
                 left: 0
               });
             } else {
               setTooltipPosition({
                 top: "100%",
                 left: 0
               });
             }
           }
         }, 0);
       };
     
       return (
         <div className="relative inline-block">
           <div
             ref={cellRef}
             onMouseEnter={handleMouseEnter}
             onMouseLeave={() => setShowTooltip(false)}
             className="cursor-pointer border-b border-dotted border-gray-400"
           >
             {text.substring(0, maxLength)}...
           </div>
           {showTooltip && (
             <div 
               ref={tooltipRef}
               className="absolute z-50 w-64 p-2 text-sm bg-gray-900 text-white rounded shadow-lg"
               style={{
                 top: tooltipPosition.top,
                 left: tooltipPosition.left,
                 maxWidth: "250px",
                 wordBreak: "break-word"
               }}
             >
               {text}
             </div>
           )}
         </div>
       );
     };

  // Function to categorize events by type
  const categorizeEvents = (data) => {
    if (!data) return { clusterEvents: [], serviceEvents: [], taskEvents: [] };
    
    const clusterEvents = data.filter(event => 
      ['CreateCluster', 'DeleteCluster', 'UpdateCluster'].includes(event.eventname)
    );
    
    const serviceEvents = data.filter(event => 
      ['CreateService', 'DeleteService', 'UpdateService'].includes(event.eventname)
    );
    
    const taskEvents = data.filter(event => 
      ['RegisterTaskDefinition', 'DeregisterTaskDefinition', 'RunTask', 'StartTask', 'StopTask'].includes(event.eventname)
    );
    
    return { clusterEvents, serviceEvents, taskEvents };
  };

  // Get the summary data directly from the parsed Athena results
  const summary = ecsData?.summary || {
    clusterOperations: { createCount: 0, deleteCount: 0, updateCount: 0 },
    serviceOperations: { createCount: 0, deleteCount: 0, updateCount: 0 },
    taskOperations: { runCount: 0, startCount: 0, stopCount: 0, registerCount: 0, deregisterCount: 0 }
  };

  // Calculate unique counts
  const calculateUniqueCounts = (data) => {
    if (!data) return { uniqueClusters: 0, uniqueServices: 0, uniqueTaskDefs: 0 };
    
    const uniqueClusters = new Set();
    const uniqueServices = new Set();
    const uniqueTaskDefs = new Set();

    data.forEach(event => {
      if (event.cluster_name) uniqueClusters.add(event.cluster_name);
      if (event.service_name) uniqueServices.add(event.service_name);
      if (event.task_definition) uniqueTaskDefs.add(event.task_definition);
    });

    return {
      uniqueClusters: uniqueClusters.size,
      uniqueServices: uniqueServices.size,
      uniqueTaskDefs: uniqueTaskDefs.size
    };
  };

  const uniqueCounts = calculateUniqueCounts(ecsData?.data || []);
  const { clusterEvents, serviceEvents, taskEvents } = categorizeEvents(ecsData?.data || []);

  // Show more/less buttons for tables
  const ShowMoreButton = ({ currentCount, totalCount, onClick, isCluster }) => {
    if (currentCount >= totalCount) return null;
    
    return (
      <div className="mt-4 text-center">
        <button
          onClick={onClick}
          className="flex items-center justify-center mx-auto px-4 py-2 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors"
        >
          <span>Show More</span>
          <ChevronDown className="h-4 w-4 ml-1" />
        </button>
      </div>
    );
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button 
            onClick={() => navigate(-1)}
            className="p-2 hover:bg-gray-100 rounded-full"
          >
            <ArrowLeft className="h-6 w-6 text-gray-600" />
          </button>
          <h1 className="text-2xl font-bold text-[#003366]">ECS Activity Details</h1>
        </div>
        <div className="text-sm text-gray-500">
          Last {duration}
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-lg p-6">
        <h2 className="text-xl font-semibold text-[#003366] mb-4">Activity Summary</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="text-sm text-gray-500">Unique Clusters</div>
            <div className="text-xl font-semibold text-[#003366]">
              {uniqueCounts.uniqueClusters || 0}
            </div>
          </div>
          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="text-sm text-gray-500">Unique Services</div>
            <div className="text-xl font-semibold text-[#003366]">
              {uniqueCounts.uniqueServices || 0}
            </div>
          </div>
          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="text-sm text-gray-500">Region</div>
            <div className="text-xl font-semibold text-[#003366]">
              {region}
            </div>
          </div>
          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="text-sm text-gray-500">Account</div>
            <div className="text-xl font-semibold text-[#003366]">
              {accountName}
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white rounded-lg shadow-lg p-6">
          <div className="flex items-center gap-3 mb-4">
            <Container className="h-6 w-6 text-[#003366]" />
            <h3 className="text-lg font-semibold text-[#003366]">Cluster Operations</h3>
          </div>
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Created</span>
              <span className="font-semibold">{summary.clusterOperations.createCount || 0}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Deleted</span>
              <span className="font-semibold">{summary.clusterOperations.deleteCount || 0}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Updated</span>
              <span className="font-semibold">{summary.clusterOperations.updateCount || 0}</span>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-lg p-6">
          <div className="flex items-center gap-3 mb-4">
            <Server className="h-6 w-6 text-[#003366]" />
            <h3 className="text-lg font-semibold text-[#003366]">Service Operations</h3>
          </div>
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Created</span>
              <span className="font-semibold">{summary.serviceOperations.createCount || 0}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Deleted</span>
              <span className="font-semibold">{summary.serviceOperations.deleteCount || 0}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Updated</span>
              <span className="font-semibold">{summary.serviceOperations.updateCount || 0}</span>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-lg p-6">
          <div className="flex items-center gap-3 mb-4">
            <ClipboardList className="h-6 w-6 text-[#003366]" />
            <h3 className="text-lg font-semibold text-[#003366]">Task Operations</h3>
          </div>
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Registered</span>
              <span className="font-semibold">{summary.taskOperations.registerCount || 0}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Deregistered</span>
              <span className="font-semibold">{summary.taskOperations.deregisterCount || 0}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Run/Start</span>
              <span className="font-semibold">
                {(summary.taskOperations.runCount || 0) + (summary.taskOperations.startCount || 0)}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Stopped</span>
              <span className="font-semibold">{summary.taskOperations.stopCount || 0}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Cluster Events Table */}
      <div className="bg-white rounded-lg shadow-lg">
        <div className="p-6">
          <div className="flex items-center gap-3 mb-4">
            <Container className="h-6 w-6 text-[#003366]" />
            <h2 className="text-xl font-semibold text-[#003366]">Cluster Events</h2>
            <span className="text-sm text-gray-500 ml-2">
              ({clusterEvents.length} events)
            </span>
          </div>
          {clusterEvents.length > 0 ? (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Event Name
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Cluster
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Username
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Source IP
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Event Time
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {clusterEvents.slice(0, clusterRowsToShow).map((event, index) => (
                      <tr key={`cluster-${index}`} className="hover:bg-gray-50">
                        <td className="px-6 py-4 text-sm text-gray-900">
                          <LongTextCell text={event.eventname} />
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900">
                          <LongTextCell text={event.cluster_name} />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {event.username}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {event.sourceipaddress}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {event.eventtime}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <ShowMoreButton
                currentCount={clusterRowsToShow}
                totalCount={clusterEvents.length}
                onClick={() => setClusterRowsToShow(prev => prev + 10)}
                isCluster={true}
              />
            </>
          ) : (
            <div className="text-center py-6 text-gray-500">No cluster events to display</div>
          )}
        </div>
      </div>

      {/* Service Events Table */}
      <div className="bg-white rounded-lg shadow-lg">
        <div className="p-6">
          <div className="flex items-center gap-3 mb-4">
            <Server className="h-6 w-6 text-[#003366]" />
            <h2 className="text-xl font-semibold text-[#003366]">Service Events</h2>
            <span className="text-sm text-gray-500 ml-2">
              ({serviceEvents.length} events)
            </span>
          </div>
          {serviceEvents.length > 0 ? (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Event Name
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Cluster
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Service
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Username
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Source IP
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Event Time
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {serviceEvents.slice(0, serviceRowsToShow).map((event, index) => (
                      <tr key={`service-${index}`} className="hover:bg-gray-50">
                        <td className="px-6 py-4 text-sm text-gray-900">
                          <LongTextCell text={event.eventname} />
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900">
                          <LongTextCell text={event.cluster_name} />
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900">
                          <LongTextCell text={event.service_name} />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {event.username}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {event.sourceipaddress}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {event.eventtime}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <ShowMoreButton
                currentCount={serviceRowsToShow}
                totalCount={serviceEvents.length}
                onClick={() => setServiceRowsToShow(prev => prev + 10)}
              />
            </>
          ) : (
            <div className="text-center py-6 text-gray-500">No service events to display</div>
          )}
        </div>
      </div>

      {/* Task Events Table */}
      <div className="bg-white rounded-lg shadow-lg">
        <div className="p-6">
          <div className="flex items-center gap-3 mb-4">
            <ClipboardList className="h-6 w-6 text-[#003366]" />
            <h2 className="text-xl font-semibold text-[#003366]">Task Events</h2>
            <span className="text-sm text-gray-500 ml-2">
              ({taskEvents.length} events)
            </span>
          </div>
          {taskEvents.length > 0 ? (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Event Name
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Cluster
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Task Definition
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Username
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Source IP
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Event Time
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {taskEvents.slice(0, taskRowsToShow).map((event, index) => (
                      <tr key={`task-${index}`} className="hover:bg-gray-50">
                        <td className="px-6 py-4 text-sm text-gray-900">
                          <LongTextCell text={event.eventname} />
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900">
                          <LongTextCell text={event.cluster_name} />
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900">
                          <LongTextCell text={event.task_definition} />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {event.username}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {event.sourceipaddress}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {event.eventtime}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <ShowMoreButton
                currentCount={taskRowsToShow}
                totalCount={taskEvents.length}
                onClick={() => setTaskRowsToShow(prev => prev + 10)}
              />
            </>
          ) : (
            <div className="text-center py-6 text-gray-500">No task events to display</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ECSPage;